import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';

export default function DashboardAuthViews() {
  const d = useDashboardSite();
  const {
    authView, setAuthView,
    email, setEmail, password, setPassword, handleLogin, loginError, setLoginError,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword, handleAcceptInvite,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading, handleForgotPassword,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword, handleResetPassword,
  } = d;

  return (
      <div className="space-y-6">

        {/* ── Sign in ─────────────────────────────────────── */}
        {authView === 'login' && (
          <Card>
            <CardHeader>
              <CardTitle>Dashboard Login</CardTitle>
              <CardDescription>Invite-only access for tenant and admin users.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleLogin}>
                {loginError ? (
                  <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                    {loginError}
                  </p>
                ) : null}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setLoginError('');
                    }}
                    type="email"
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setLoginError('');
                    }}
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Button type="submit">Sign in</Button>
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                    onClick={() => { setAuthView('forgot'); setForgotSubmitted(false); setForgotEmail(email); setLoginError(''); }}
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Forgot password ─────────────────────────────── */}
        {authView === 'forgot' && (
          <Card>
            <CardHeader>
              <CardTitle>Reset your password</CardTitle>
              <CardDescription>We'll email you a secure link to choose a new password.</CardDescription>
            </CardHeader>
            <CardContent>
              {forgotSubmitted ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-primary/20 bg-primary/8 px-4 py-4 text-sm space-y-1">
                    <p className="font-medium text-foreground">Check your inbox</p>
                    <p className="text-muted-foreground">
                      If that email is registered, you'll receive a reset link shortly. Check your spam folder if you don't see it.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setAuthView('login'); setForgotSubmitted(false); setForgotEmail(''); }}
                  >
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleForgotPassword}>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <Button type="submit" disabled={isForgotLoading}>
                      {isForgotLoading ? 'Sending…' : 'Send reset link'}
                    </Button>
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                      onClick={() => { setAuthView('login'); setForgotEmail(''); }}
                    >
                      Back to sign in
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Set new password (from email link) ─────────── */}
        {authView === 'reset' && (
          <Card>
            <CardHeader>
              <CardTitle>Set a new password</CardTitle>
              <CardDescription>The reset link is valid for 24 hours and can only be used once.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleResetPassword}>
                <div className="space-y-2">
                  <Label>New password</Label>
                  <PasswordInput
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm new password</Label>
                  <PasswordInput
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Button type="submit" disabled={isResettingPassword}>
                    {isResettingPassword ? 'Saving…' : 'Set new password'}
                  </Button>
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                    onClick={() => { setResetToken(''); setResetNewPassword(''); setResetConfirmPassword(''); setAuthView('login'); }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Accept invite (only shown on the sign-in view) ── */}
        {authView === 'login' && (
          <Card>
            <CardHeader>
              <CardTitle>Accept Invite</CardTitle>
              <CardDescription>Use invite token to create your account.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleAcceptInvite}>
                <div className="space-y-2">
                  <Label>Invite token</Label>
                  <Input value={inviteToken} onChange={(e) => setInviteToken(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Full name (optional)</Label>
                  <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>New password</Label>
                  <PasswordInput
                    value={invitePassword}
                    onChange={(e) => setInvitePassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <Button type="submit" variant="secondary">Accept invite</Button>
              </form>
            </CardContent>
          </Card>
        )}

      </div>

  );
}
